import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
            const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
            
            // Get the current origin for redirect URL
            const redirectTo = `${window.location.origin}/reset-password`;

            const response = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${supabaseUrl}/auth/v1/recover`, true);
                xhr.setRequestHeader('Content-Type', 'application/json');
                xhr.setRequestHeader('apikey', supabaseKey);
                
                xhr.onload = function() {
                    try {
                        const data = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({ ok: true, data });
                        } else {
                            resolve({ ok: false, data });
                        }
                    } catch (e) {
                        // Empty response is OK for password recovery
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({ ok: true, data: {} });
                        } else {
                            reject(new Error('Failed to parse response'));
                        }
                    }
                };
                
                xhr.onerror = function() {
                    reject(new Error('Network error'));
                };
                
                xhr.send(JSON.stringify({ email, redirect_to: redirectTo }));
            });

            if (!response.ok) {
                throw new Error(response.data?.msg || response.data?.error_description || 'Failed to send reset email');
            }

            setSuccess(true);
        } catch (err) {
            console.error('Password reset error:', err);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <Card className="bg-white border-gray-200 shadow-sm text-center">
                        <CardHeader>
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <CardTitle className="font-heading text-xl text-gray-900">Verifică email-ul</CardTitle>
                            <CardDescription className="text-gray-500">
                                Am trimis un link de resetare a parolei la <strong>{email}</strong>. 
                                Verifică inbox-ul și folder-ul spam.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Button 
                                onClick={() => setSuccess(false)} 
                                variant="outline" 
                                className="w-full"
                                data-testid="resend-reset-btn"
                            >
                                Trimite din nou
                            </Button>
                            <Link to="/login">
                                <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Înapoi la Login
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-2 mb-8">
                    <img 
                        src="https://blog.mersi.ai/wp-content/uploads/2025/05/mersi.ai-logo-1024px.jpg" 
                        alt="Mersi.ai Logo" 
                        className="w-10 h-10 rounded object-cover"
                    />
                    <span className="font-heading text-xl text-gray-900">VibeCoder</span>
                </Link>

                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="font-heading text-xl text-gray-900">Resetare parolă</CardTitle>
                        <CardDescription className="text-gray-500">
                            Introdu adresa de email și îți vom trimite un link pentru a-ți reseta parola
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="forgot-error">
                                    {error}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-700">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="email@exemplu.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 bg-white border-gray-200 text-gray-900"
                                        required
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                disabled={loading}
                                data-testid="forgot-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Se trimite...
                                    </>
                                ) : (
                                    'Trimite link de resetare'
                                )}
                            </Button>

                            <div className="text-center pt-2">
                                <Link 
                                    to="/login" 
                                    className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                                    data-testid="back-to-login-link"
                                >
                                    <ArrowLeft className="w-3 h-3" />
                                    Înapoi la Login
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ForgotPassword;
